import React from 'react';
import { Pressable, View } from 'react-native';
import { Feather } from '@expo/vector-icons';

import Text from '../../../components/text/Text';
import { useTheme } from '../../../theme';
import { useThemedStyles } from '../../../hooks/useThemedStyles';

/**
 * RosterRow: one shift, open shift, timesheet or leave request in the roster pages.
 * Props:
 * - title (date), details: secondary lines (falsy entries are skipped)
 * - children: extra content under the details (status badge)
 * - right: node on the right (status badge or button)
 * - onPress: makes the row a button with a chevron; accessibilityLabel, accessibilityHint
 */
const RosterRow = ({
	title,
	details = [],
	children,
	right,
	onPress,
	accessibilityLabel,
	accessibilityHint,
}) => {
	const styles = useThemedStyles(createStyles);
	const { theme: { ui: { colors } } } = useTheme();
	const visibleDetails = details.filter(Boolean);

	const content = (
		<>
			<View style={styles.body}>
				<Text style={styles.title}>{title}</Text>
				{visibleDetails.map((detail, index) => (
					<Text key={`${index}-${detail}`} style={styles.detail}>{detail}</Text>
				))}
				{children}
			</View>
			{Boolean(right) && <View style={styles.right}>{right}</View>}
			{Boolean(onPress) && <Feather name="chevron-right" size={22} color={colors.iconMuted} />}
		</>
	);

	if (!onPress) {
		return <View style={[styles.row, Boolean(right) && styles.rowWithAction]}>{content}</View>;
	}

	return (
		<Pressable
			onPress={onPress}
			accessibilityRole="button"
			accessibilityLabel={accessibilityLabel}
			accessibilityHint={accessibilityHint}
			style={({ pressed }) => [styles.row, pressed && styles.pressed]}
		>
			{content}
		</Pressable>
	);
};

const createStyles = ({ ui: { colors, radius, space, touch, type } }) => ({
	row: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: space.md,
		minHeight: touch.rowWithSubtitle,
		paddingVertical: 10,
		paddingHorizontal: 14,
		borderRadius: radius.card,
		borderWidth: 1,
		borderColor: colors.raisedBorder,
		backgroundColor: colors.raised,
	},
	rowWithAction: {
		paddingRight: 10,
	},
	pressed: {
		opacity: 0.85,
	},
	body: {
		flex: 1,
		gap: 2,
	},
	right: {
		alignSelf: 'center',
	},
	title: {
		...type.rowTitleSemibold,
		color: colors.text,
	},
	detail: {
		...type.secondary,
		color: colors.textSecondary,
	},
});

export default RosterRow;
